if (!customElements.get("m-science-charts")) {
  class MScienceCharts extends HTMLElement {
    constructor() {
      super();
      this.hasAnimated = false;
      this.observer = null;
      this.rafId = null;
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.chart = this.querySelector("[data-sc-chart]");
      this.bars = this.querySelectorAll("[data-sc-bar]");
      this.counters = this.querySelectorAll("[data-sc-count]");
      this.connector = this.querySelector("[data-sc-connector]");
      this.baselineFrame = this.querySelector(".m-science-charts__col--baseline .m-science-charts__bar-frame");
      this.biogoldFrame = this.querySelector(".m-science-charts__col--biogold .m-science-charts__bar-frame");
      this.section = this.closest(".m-science-charts");
      this.duration = Number(this.dataset.duration) || 1100;
      this.baselinePct = Number(this.dataset.baselinePct) || 25;
      this.biogoldPct = Number(this.dataset.biogoldPct) || 92;

      this.updateConnector(0, 0);
      window.addEventListener("resize", this.onResize);

      if (this.prefersReducedMotion()) {
        this.animate(true);
        return;
      }

      if ("IntersectionObserver" in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.animate(false);
                this.observer.disconnect();
              }
            });
          },
          { rootMargin: "0px 0px -12% 0px", threshold: 0.2 }
        );
        this.observer.observe(this.chart || this);
      } else {
        this.animate(false);
      }

      if (window.Shopify && Shopify.designMode) {
        this._onSectionLoad = (event) => {
          if (event.target && event.target.contains(this)) {
            this.hasAnimated = false;
            this.classList.remove("is-animated");
            if (this.section) this.section.classList.remove("is-animated");
            this.resetCounters();
            this.updateConnector(0, 0);
            this.animate(this.prefersReducedMotion());
          }
        };
        document.addEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener("resize", this.onResize);
      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this._onSectionLoad) {
        document.removeEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    onResize() {
      if (this.hasAnimated) {
        this.updateConnector(this.baselinePct, this.biogoldPct);
      }
    }

    resetCounters() {
      this.counters.forEach((el) => {
        el.textContent = "0";
      });
      this.bars.forEach((bar) => {
        bar.style.height = "0";
      });
    }

    easeOutCubic(t) {
      return 1 - Math.pow(1 - t, 3);
    }

    /**
     * Position the dashed connector between current bar fill tops.
     * @param {number} baselineFillPct - current baseline fill as % of frame height
     * @param {number} biogoldFillPct - current biogold fill as % of frame height
     */
    updateConnector(baselineFillPct, biogoldFillPct) {
      if (!this.connector || !this.baselineFrame || !this.biogoldFrame) return;

      const plot = this.querySelector(".m-science-charts__plot");
      if (!plot) return;

      const plotRect = plot.getBoundingClientRect();
      const baseRect = this.baselineFrame.getBoundingClientRect();
      const goldRect = this.biogoldFrame.getBoundingClientRect();

      if (!plotRect.width || !baseRect.width || !goldRect.width) return;

      const startX = baseRect.right - plotRect.left;
      const endX = goldRect.left - plotRect.left;
      const startY = baseRect.top - plotRect.top + baseRect.height * (1 - baselineFillPct / 100);
      const endY = goldRect.top - plotRect.top + goldRect.height * (1 - biogoldFillPct / 100);

      const dx = endX - startX;
      const dy = endY - startY;
      const length = Math.sqrt(dx * dx + dy * dy);
      const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

      this.connector.style.left = `${startX}px`;
      this.connector.style.top = `${startY}px`;
      this.connector.style.setProperty("--sc-connector-length", `${Math.max(length, 0)}px`);
      this.connector.style.setProperty("--sc-connector-angle", `${angle}deg`);
    }

    animate(instant) {
      if (this.hasAnimated && !instant) return;
      this.hasAnimated = true;

      requestAnimationFrame(() => {
        this.classList.add("is-animated");
        if (this.section) this.section.classList.add("is-animated");

        if (instant) {
          this.counters.forEach((el) => {
            const target = el.dataset.scTarget || "0";
            el.textContent = target;
          });
          this.bars.forEach((bar) => {
            bar.style.height = "var(--sc-target-height)";
          });
          this.updateConnector(this.baselinePct, this.biogoldPct);
          return;
        }

        this.runTimeline(this.duration);
      });
    }

    runTimeline(duration) {
      const start = performance.now();
      const counters = Array.from(this.counters).map((el) => {
        const raw = el.dataset.scTarget || "0";
        const target = Number(raw);
        return {
          el,
          raw,
          target,
          isNumber: !Number.isNaN(target),
          isInteger: Number.isInteger(target),
        };
      });

      // Slight lag on the gold bar so the line climbs into it
      const baselineDelay = 0;
      const biogoldDelay = 0.12;

      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);

        const baselineT = this.easeOutCubic(Math.min(Math.max((progress - baselineDelay) / (1 - baselineDelay), 0), 1));
        const biogoldT = this.easeOutCubic(Math.min(Math.max((progress - biogoldDelay) / (1 - biogoldDelay), 0), 1));

        const baselineFill = this.baselinePct * baselineT;
        const biogoldFill = this.biogoldPct * biogoldT;

        this.bars.forEach((bar) => {
          const kind = bar.dataset.scBar;
          if (kind === "baseline") {
            bar.style.height = `${baselineFill}%`;
          } else if (kind === "biogold") {
            bar.style.height = `${biogoldFill}%`;
          }
        });

        this.updateConnector(baselineFill, biogoldFill);

        counters.forEach((item) => {
          if (!item.isNumber) {
            item.el.textContent = item.raw;
            return;
          }
          const value = item.target * this.easeOutCubic(progress);
          item.el.textContent = item.isInteger ? String(Math.round(value)) : String(Math.round(value));
        });

        if (progress < 1) {
          this.rafId = requestAnimationFrame(tick);
        } else {
          this.bars.forEach((bar) => {
            bar.style.height = "var(--sc-target-height)";
          });
          this.updateConnector(this.baselinePct, this.biogoldPct);
          counters.forEach((item) => {
            item.el.textContent = item.isNumber
              ? item.isInteger
                ? String(item.target)
                : String(item.target)
              : item.raw;
          });
        }
      };

      this.rafId = requestAnimationFrame(tick);
    }
  }

  customElements.define("m-science-charts", MScienceCharts);
}
